import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Loader2, MessageCircle, CalendarPlus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * ApplicationChat — private thread scoped to a single job application.
 *   - Employers and applicants are the only two participants.
 *   - Lives inside the application context; kept separate from general DMs.
 *
 * Props:
 *   appId          — application id (required)
 *   compact?       — smaller header (used in inline expandable rows)
 *   pollMs?        — poll interval for new messages (default 8s while open)
 */
export default function ApplicationChat({ appId, compact = false, pollMs = 8000 }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, msgs: [], other: null, role: "" });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const listRef = useRef(null);

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  const load = async (silent = false) => {
    try {
      if (!silent) setState(s => ({ ...s, loading: true }));
      const { data } = await api.get(`/jobs/applications/${appId}/messages`);
      setState({ loading: false, msgs: data.messages || [], other: data.other, role: data.role });
      setErr("");
      if (!silent) scrollBottom();
    } catch (e) {
      setErr(formatErr(e));
      setState(s => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { if (appId) load(); /* eslint-disable-next-line */ }, [appId]);

  // Light polling so both sides see new messages without a page reload
  useEffect(() => {
    if (!appId) return;
    const t = setInterval(() => load(true), pollMs);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [appId, pollMs]);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setErr("");
    // optimistic append
    const optimistic = {
      id: `tmp-${Date.now()}`, from_user_id: user.id, text: t,
      created_at: new Date().toISOString(), _optimistic: true,
    };
    setState(s => ({ ...s, msgs: [...s.msgs, optimistic] }));
    setText("");
    scrollBottom();
    try {
      const { data } = await api.post(`/jobs/applications/${appId}/messages`, { text: t });
      setState(s => ({
        ...s,
        msgs: [...s.msgs.filter(m => m.id !== optimistic.id), data],
      }));
      scrollBottom();
    } catch (e2) {
      // rollback
      setState(s => ({ ...s, msgs: s.msgs.filter(m => m.id !== optimistic.id) }));
      setErr(formatErr(e2));
    } finally { setSending(false); }
  };

  // Interview scheduling (kept above the early-return so hooks order is stable)
  const [showInterviewPicker, setShowInterviewPicker] = useState(false);
  const [interviewAt, setInterviewAt] = useState("");
  const [savingInterview, setSavingInterview] = useState(false);

  const saveInterview = async () => {
    if (!interviewAt) { toast.error("Pick a date & time"); return; }
    setSavingInterview(true);
    try {
      const iso = new Date(interviewAt).toISOString();
      await api.post(`/jobs/applications/${appId}/interview`, { interview_at: iso });
      toast.success("Interview scheduled — both of you will see it on your dashboards.");
      setShowInterviewPicker(false);
      try {
        await api.post(`/jobs/applications/${appId}/messages`, {
          text: `📅 Interview scheduled for ${new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`,
        });
      } catch { /* non-fatal */ }
      load(true);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSavingInterview(false); }
  };

  if (!user) {
    return <p className="text-sm text-[#8A8A8A]">Please sign in to view this chat.</p>;
  }

  const other = state.other || {};

  return (
    <div className="rounded-2xl border border-[#E5E5E3] bg-white overflow-hidden" data-testid={`app-chat-${appId}`}>
      <div className={`flex items-center gap-2 px-4 ${compact ? "py-2.5" : "py-3"} border-b border-[#E5E5E3] bg-[#FAFAF8]`}>
        <MessageCircle size={14} className="text-[#0057FF]"/>
        <p className="text-sm font-medium flex-1 min-w-0 truncate">
          Chat with <span className="text-[#0057FF]">{other.name || "…"}</span>
          <span className="text-[#8A8A8A] font-normal"> · {state.role === "employer" ? "applicant" : "recruiter"}</span>
        </p>
        <button onClick={() => setShowInterviewPicker(v => !v)}
                className="btn-ghost !py-1 !px-2 text-[11px]"
                data-testid={`app-chat-schedule-${appId}`}
                title="Schedule an interview">
          <CalendarPlus size={12}/> Schedule
        </button>
      </div>

      {showInterviewPicker && (
        <div className="px-4 py-3 border-b border-[#E5E5E3] bg-[#EEF3FF]/40 flex items-center gap-2 flex-wrap" data-testid={`app-chat-schedule-picker-${appId}`}>
          <input type="datetime-local" className="input !py-1.5 !text-sm max-w-[220px]"
                 value={interviewAt} onChange={(e)=>setInterviewAt(e.target.value)}
                 data-testid={`app-chat-schedule-input-${appId}`}/>
          <button onClick={saveInterview} disabled={savingInterview} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`app-chat-schedule-save-${appId}`}>
            {savingInterview ? <Loader2 size={12} className="animate-spin"/> : "Confirm"}
          </button>
          <button onClick={() => setShowInterviewPicker(false)} className="btn-ghost !py-1.5 !px-2 text-xs" data-testid={`app-chat-schedule-cancel-${appId}`}>
            <X size={12}/>
          </button>
        </div>
      )}

      <div ref={listRef} className="max-h-72 overflow-y-auto px-4 py-3 space-y-2 bg-white" data-testid="app-chat-list">
        {state.loading && (
          <p className="text-xs text-[#8A8A8A] flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading…</p>
        )}
        {!state.loading && state.msgs.length === 0 && (
          <p className="text-xs text-[#8A8A8A]">No messages yet. Say hi 👋 — this thread stays inside this application.</p>
        )}
        {state.msgs.map(m => {
          const mine = m.from_user_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}
                 data-testid={`app-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm break-words
                ${mine ? "bg-[#0057FF] text-white rounded-br-md" : "bg-[#F2F2F0] text-[#0A0A0A] rounded-bl-md"}
                ${m._optimistic ? "opacity-70" : ""}`}>
                <p className="whitespace-pre-wrap">{m.text}</p>
                <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-[#8A8A8A]"}`}>
                  {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {err && <p className="text-xs text-[#B84B4B] px-4 py-1.5" data-testid="app-chat-error">{err}</p>}

      <form onSubmit={send} className="flex items-center gap-2 border-t border-[#E5E5E3] p-2 bg-white">
        <input
          value={text}
          onChange={(e)=>setText(e.target.value)}
          placeholder={`Message ${other.name?.split(" ")[0] || "them"}…`}
          className="input flex-1"
          maxLength={4000}
          data-testid="app-chat-input"
        />
        <button type="submit" disabled={!text.trim() || sending}
                className="btn-primary !py-2 !px-3" data-testid="app-chat-send">
          {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
        </button>
      </form>
    </div>
  );
}
